/**
 * fieldOutbox (Inventory Screens Step 4.3a) — the field-zone capture outbox.
 *
 * PRODUCER ONLY. Constructs and queues syncQueue items in the tmg-field DB
 * (store declared-empty since 4.2a): 'field_capture' (4.3a), 'media_add' and
 * 'media_remove' (4.3d-B). The type-aware drain that replays them over the
 * network is 4.3b/4.3d-A; the edit UI that marks records dirty is Step 5.
 * Mirrors the legacy offlineEngine item SHAPE (status/retry_count/created_at/
 * error) but shares zero code with it — that engine belongs to the old screens.
 *
 * Item schemas:
 *   { outbox_id, type: 'field_capture', record_id, tool_id, site_id,
 *     payload,                    // buildCapturePayload(record) result
 *     status: 'pending', retry_count: 0, created_at, error: null }
 *   { outbox_id, type: 'media_add', record_id, tool_id, site_id, photo_type,
 *     blob_data,                  // base64; the sender POSTs it as file_data
 *     file_ext, disposal_id, status/retry_count/created_at/error }
 *   { outbox_id, type: 'media_remove', record_id, tool_id, site_id,
 *     media_id,                   // the only key the sender reads
 *     status/retry_count/created_at/error }
 *
 * outbox_id is time-prefixed (`${Date.now()}-${rand36}`) so the pk is FIFO —
 * the drain replays with orderBy(':id'), no timestamp index needed (v1
 * schema holds).
 *
 * Coalescing is FIELD_CAPTURE-ONLY (D-4.AF): a fresh capture diff is always
 * baseline→current-working, so a new pending field_capture fully supersedes
 * an older PENDING — or, since D-5 (Path 2), a lingering CONFLICT —
 * field_capture for the same record, deleted in the same transaction. Media
 * items sharing that record_id are NEVER coalesced: every queued photo
 * add/remove is an independent unit of work, and superseding one would
 * silently drop an unsynced photo. Items in 'error'/'not_found'/'failed' are
 * review-gated (Step 5 owns that flow) and are never touched.
 */

import { fieldDb } from '../db/fieldDb';
import { ensureOpen } from '../db/fieldStore';
import { buildCapturePayload } from './buildCapturePayload';
import { rebaseRecordFromServer } from './rebaseRecord';
import { isPendingRecord } from './pendingId';
import { isFieldDrainActive, setFieldCaptureInFlight, fieldCaptureInFlight } from './drainLatch';

function mintOutboxId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Gate 3 (5.4 A-1): the enqueue→drain nudge. Every SUCCESSFUL enqueue
 * dispatches this window event; useFieldDrain listens and runs a sync
 * cycle, so an item queued while already online drains immediately instead
 * of waiting for the next mount / 'online' event. A window event (not a
 * hook return) because producers also run in bare-module context — the D.3
 * follow-up capture fires from inside the reconcile sweep. Never dispatched
 * on failure (every producer's catch rethrows before it) and never on the
 * nothing-queued early returns.
 */
export const FIELD_ENQUEUED_EVENT = 'tmg-field-enqueued';

function notifyEnqueued() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(FIELD_ENQUEUED_EVENT));
  }
}

// Fields each enqueue path writes. retryFailedItem rebuilds an item from
// this list, dropping everything a sender arm added. ⚠️ If you add a field
// to an enqueue function below, add it here too — otherwise retry drops it.
const ENQUEUE_FIELDS = {
  field_capture: ['tool_id', 'payload'],
  tool_create:   ['supplier_tool_id', 'oem', 'caller_email'],
  media_add:     ['tool_id', 'photo_type', 'blob_data', 'byte_size',
                  'file_ext', 'disposal_id'],
  media_remove:  ['tool_id', 'media_id'],
};
const ENQUEUE_COMMON = ['outbox_id', 'type', 'record_id', 'site_id', 'created_at'];

/**
 * The statuses a queue item can be Retried or Discarded from (D-39).
 *
 * ONE definition, three consumers: retryFailedItem's guard,
 * discardFailedItem's guard, and FieldSyncSheet's canActOnFailed. Three
 * independent `status === 'failed'` string comparisons is exactly how the gap
 * this widen closes was born: 'failed' was actionable while 'error' and
 * 'not_found' were not, so an item that terminated on a 403 (auth_denied), a
 * 404 (tool_not_found), or a capture not_found sat in needs-attention forever
 * with no way forward and no way out. For a media_add that meant the queue row
 * held the ONLY copy of a photo, unreachable. Add a status here and all three
 * gates widen together; edit one comparison in isolation and the bug returns.
 *
 * Deliberately NOT reason-conditional: every listed status is actionable
 * whatever its `error` value. A rep facing a stuck row needs an exit, and
 * which server fact produced it is not something the UI can adjudicate.
 */
export const ACTIONABLE_STATUSES = ['failed', 'error', 'not_found'];

/**
 * enqueueFieldCapture's third answer: the capture was HELD, not queued
 * (same-row race fix, 2026-10-28, spec D-6). Distinct from null, which means
 * "nothing to send" to both callers that read the return — Save's saveOutcome
 * and re-apply's empty_diff arm — and truthy, so Save reports it as queued:
 * the rep's value is captured and goes out by itself once the record settles.
 */
export const CAPTURE_HELD = 'capture-held';

// The outcomes after which a sent capture will never confirm. D-7: on any of
// these a held request is dropped from the row and its tick stays dirty.
const TERMINAL_OUTCOMES = new Set(['conflict', 'not_found', 'error', 'failed']);

/*
 * Does this record have a capture that has not settled — the one the drain is
 * sending right now, or a 'saved' one reconcile has not yet stamped onto the row?
 * Building a second capture from the row in either window is the same-row race:
 * the second carries an etag the first is about to make stale (Gate 0, cases 1-2).
 * C-1: 'conflict' is NEVER unsettled. Re-apply clears a conflict by enqueueing
 * through this function, so counting it would block the only recovery there is.
 * C-2: per record — never isFieldDrainActive(), which spans every record.
 *
 * Called inside a syncQueue transaction. ORDER IS LOAD-BEARING: the queue read
 * comes first and the in-memory check after it. That read cannot run until any
 * earlier syncQueue transaction has committed, so if the drain's claim (also a
 * syncQueue transaction) got there first, its in-flight mark is already set.
 */
async function hasUnsettledCapture(recordId) {
  const saved = await fieldDb.syncQueue
    .where('record_id')
    .equals(recordId)
    .filter((i) => i.type === 'field_capture' && i.status === 'saved')
    .count();
  return saved > 0 || fieldCaptureInFlight() === recordId;
}

/**
 * Build the capture payload for a record and queue it.
 *
 * Same-row race fix (2026-10-28, spec D-1/D-6): when the record has an
 * unsettled capture (hasUnsettledCapture), nothing is built. The row is marked
 * `capture_held` instead — persisted, so an app kill cannot turn a captured
 * tick into a plain unsent edit (C-4) — and its dirty keys wait there. The
 * mark is spent by releaseHeldCapture: from reconcile once the sibling's etag
 * is stamped, or from settleFieldCapture when the sibling stops being in flight
 * without settling (C-3). A capture that IS queued carries every dirty key, so
 * it clears any mark on the row.
 *
 * @param {Object} record — a fieldDb records row (baseline/working/dirty + children)
 * @returns {Promise<string|null>} the new outbox_id; CAPTURE_HELD when the
 *   capture was held; or null when the record has nothing to send (empty diff
 *   — nothing is queued).
 */
export async function enqueueFieldCapture(record) {
  // D-5.2b-E: a temp-id record (not yet created server-side) must NEVER
  // enqueue a capture — it has no real tool_id/etag and would hard-400.
  // Its dirt flushes via the post-create reconcile follow-up instead.
  if (isPendingRecord(record.record_id)) {
    console.warn(`fieldOutbox.enqueueFieldCapture: refused temp-id record ${record.record_id} — dirt rides the tool_create reconcile follow-up (D-5.2b-E)`);
    return null;
  }
  const payload = buildCapturePayload(record);
  if (payload === null) return null; // empty capture must never be queued

  const item = {
    outbox_id: mintOutboxId(),
    type: 'field_capture',
    record_id: record.record_id,
    tool_id: record.tool_id,
    site_id: record.site_id,
    payload,
    status: 'pending',
    retry_count: 0,
    created_at: new Date().toISOString(),
    error: null,
  };

  try {
    await ensureOpen();
    let held = false;
    // records joins the scope for the capture_held mark (2026-10-28). Callers
    // already inside a records+syncQueue transaction (reconcile's tool_create
    // follow-up) nest within it.
    await fieldDb.transaction('rw', fieldDb.records, fieldDb.syncQueue, async () => {
      if (await hasUnsettledCapture(record.record_id)) {
        const row = await fieldDb.records.get(record.record_id);
        if (row) {
          row.capture_held = true;
          await fieldDb.records.put(row);
        }
        held = true;
        return;
      }
      // Coalesce: a newer diff supersedes an older PENDING one for the same
      // record — FIELD_CAPTURE ONLY (D-4.AF): media items share record_id
      // for UI joins and must never be superseded (silent photo loss).
      // D-5 (Path 2): a lingering CONFLICT field_capture is superseded too —
      // the fresh capture is the rep's current intent for the record, so
      // re-apply (and a manual re-Save) clears the conflict by enqueue alone.
      // error/not_found/failed items stay review-gated — never touched.
      // A pending item the drain has claimed is never reached here: it is in
      // flight, so the record is unsettled and the branch above held instead.
      await fieldDb.syncQueue
        .where('record_id')
        .equals(record.record_id)
        .filter((i) => i.type === 'field_capture'
          && (i.status === 'pending' || i.status === 'conflict'))
        .delete();
      await fieldDb.syncQueue.put(item);
      const row = await fieldDb.records.get(record.record_id);
      if (row && row.capture_held) {
        delete row.capture_held;
        await fieldDb.records.put(row);
      }
    });
    if (held) return CAPTURE_HELD;
    notifyEnqueued();
    return item.outbox_id;
  } catch (err) {
    // Local IDB write — a genuine failure must surface, never be swallowed.
    console.error('fieldOutbox.enqueueFieldCapture failed:', err);
    throw err;
  }
}

/**
 * The drain's claim on one field_capture, just before it is sent (same-row race
 * fix, 2026-10-28). The drain reads its list once, at the start of a cycle, so an
 * item further down that list can be coalesced away by a later enqueue before the
 * drain reaches it; sending it then would apply a value whose confirmation lands
 * on nothing — Gate 0's step 3 by another route. The existence check and the
 * in-flight mark share ONE syncQueue transaction, so an enqueue either commits
 * before it (the item is gone: false, skip it — its replacement drains next
 * cycle) or runs after it (it sees the record in flight and holds).
 *
 * @returns {Promise<boolean>} true when the item is still queued and now claimed.
 */
export async function claimFieldCapture(item) {
  await ensureOpen();
  return fieldDb.transaction('rw', fieldDb.syncQueue, async () => {
    const live = await fieldDb.syncQueue.get(item.outbox_id);
    if (!live) return false;
    setFieldCaptureInFlight(item.record_id);
    return true;
  });
}

/**
 * Spend a held request: queue the row's capture now. Its payload is every dirty
 * key, the held tick included, built from the row's current etag. A no-op when
 * the row carries no mark. Called by reconcile after it stamps a confirmed
 * capture's etag, and by settleFieldCapture on a halt (C-3).
 */
export async function releaseHeldCapture(recordId) {
  await ensureOpen();
  const row = await fieldDb.records.get(recordId);
  if (!row || !row.capture_held) return null;
  const outcome = await enqueueFieldCapture(row);
  if (outcome === null) {
    // Nothing left to send (the dirt was confirmed by the sibling): the mark
    // has no capture to wait for, so it goes.
    await fieldDb.transaction('rw', fieldDb.records, async () => {
      const fresh = await fieldDb.records.get(recordId);
      if (fresh && fresh.capture_held) {
        delete fresh.capture_held;
        await fieldDb.records.put(fresh);
      }
    });
  }
  return outcome;
}

/**
 * The drain's half after a claimed field_capture comes back, on every outcome
 * (it runs in the drain's finally). The in-flight mark goes first, so nothing
 * below sees the record as in flight. Then, for a row holding a request:
 *   - 'saved': nothing here. The record is still unsettled until reconcile
 *     stamps the new etag, and reconcile releases the request after it does.
 *   - terminal (D-7): the mark is dropped and the tick stays dirty — never
 *     shipped off a capture that will not confirm, never destroyed. The next
 *     tap or Save carries it out.
 *   - anything else — a transport or auth halt, a retry, a throw (C-3): the
 *     sibling is no longer in flight and has not settled, so the hold has
 *     nothing left to wait for. Released now; the new capture coalesces with
 *     the still-pending sibling and carries both.
 */
export async function settleFieldCapture(recordId, outcome) {
  setFieldCaptureInFlight(null);
  if (outcome === 'saved') return;
  try {
    if (TERMINAL_OUTCOMES.has(outcome)) {
      await ensureOpen();
      await fieldDb.transaction('rw', fieldDb.records, async () => {
        const row = await fieldDb.records.get(recordId);
        if (row && row.capture_held) {
          delete row.capture_held;
          await fieldDb.records.put(row);
        }
      });
      return;
    }
    await releaseHeldCapture(recordId);
  } catch (err) {
    // Never let the release fail the drain: the mark is persisted, and the
    // next reconcile or enqueue for the record still spends it.
    console.error(`fieldOutbox.settleFieldCapture failed for ${recordId}:`, err);
  }
}

/**
 * Queue a NEW-TOOL create for offline replay (5.2b — D-5.2b-B/C). Routed by
 * the drain's tool_create arm to update_tool POST (the existing audited,
 * air-gapped create endpoint — server mints tool-{uuid8} and stamps
 * added_by_tmg/found). Plain put, NO coalesce — every create is an
 * independent unit of work; the coalescer stays field_capture-scoped.
 * record_id is the CLIENT temp id ('pending-' + uuid); reconcile rewrites
 * it to the server identity (D-5.2b-D).
 *
 * @returns {Promise<string>} the new outbox_id
 */
export async function enqueueToolCreate({ record_id, site_id, supplier_tool_id, oem, caller_email }) {
  if (!record_id || !site_id || !supplier_tool_id) {
    throw new Error('enqueueToolCreate requires record_id, site_id, and supplier_tool_id');
  }
  const item = {
    outbox_id: mintOutboxId(),
    type: 'tool_create',
    record_id,                       // temp 'pending-*' id — re-keyed at reconcile
    site_id,
    supplier_tool_id,                // verbatim; server compares normalized
    oem: oem ?? null,
    caller_email: caller_email ?? null,
    status: 'pending', retry_count: 0, created_at: new Date().toISOString(), error: null,
  };
  try {
    await ensureOpen();
    await fieldDb.syncQueue.put(item);
    notifyEnqueued();
    return item.outbox_id;
  } catch (err) {
    console.error('fieldOutbox.enqueueToolCreate failed:', err);
    throw err;
  }
}

/**
 * Queue a photo ADD for offline replay (4.3d-B). NO coalescing — every queued
 * photo is an independent unit of work (D-4.AF). The blob rides the item as
 * base64 `blob_data`; sendMediaAdd renames it to `file_data` at POST time.
 * record_id/site_id are UI-join / contract-parity carries — the backend keys
 * on tool_id and resolves the site from the tool.
 *
 * @returns {Promise<string>} the new outbox_id
 */
export async function enqueueMediaAdd({
  record_id, tool_id, site_id, photo_type, blob_data, file_ext, disposal_id, byte_size,
}) {
  if (!tool_id || !blob_data || !file_ext) {
    throw new Error('enqueueMediaAdd requires tool_id, blob_data, and file_ext');
  }
  const item = {
    outbox_id: mintOutboxId(),
    type: 'media_add',
    record_id,   // UI-join / counts; NOT read by the sender
    tool_id,     // backend keys on this
    site_id,     // contract-parity; backend resolves site from the tool
    photo_type,  // open-vocab; passed through
    blob_data,   // sender renames to file_data at POST (D-4.AA)
    byte_size: byte_size ?? blob_data.length,
                                 // D-7: REAL blob bytes when the caller supplies
                                 // them (the Phase 2 capture seam does). The
                                 // base64 fallback stays for callers that do
                                 // not, and is ~1.37x the true size. Unindexed,
                                 // forward-looking seed for the blob-table
                                 // split. Readers MUST still fall back to
                                 // blob_data.length — items queued before this
                                 // field existed are on devices today. NOTE the
                                 // unit is now mixed across a device's queue;
                                 // FieldSyncSheet's total is affected (backlog).
    file_ext,
    disposal_id: disposal_id ?? null, // sender drops null via ?? undefined
    status: 'pending', retry_count: 0, created_at: new Date().toISOString(), error: null,
  };
  try {
    await ensureOpen();
    await fieldDb.syncQueue.put(item); // media NEVER coalesces (D-4.AF)
    notifyEnqueued();
    return item.outbox_id;
  } catch (err) {
    console.error('fieldOutbox.enqueueMediaAdd failed:', err);
    throw err;
  }
}

/**
 * Queue a photo REMOVE for offline replay (4.3d-B). Requires a real media_id —
 * an offline-added, not-yet-uploaded photo has none; cancel that with
 * cancelPendingMediaAdd instead (D-4.AG). No coalescing.
 *
 * @returns {Promise<string>} the new outbox_id
 */
export async function enqueueMediaRemove({ record_id, tool_id, site_id, media_id }) {
  if (!media_id) throw new Error('enqueueMediaRemove requires media_id');
  const item = {
    outbox_id: mintOutboxId(),
    type: 'media_remove',
    record_id, tool_id, site_id, // UI-join carry; sender reads only media_id
    media_id,
    status: 'pending', retry_count: 0, created_at: new Date().toISOString(), error: null,
  };
  try {
    await ensureOpen();
    await fieldDb.syncQueue.put(item);
    notifyEnqueued();
    return item.outbox_id;
  } catch (err) {
    console.error('fieldOutbox.enqueueMediaRemove failed:', err);
    throw err;
  }
}

/**
 * Local-cancel of a PENDING media_add (D-4.AG): the photo never reached the
 * server, so removing it is a pure dequeue. Returns false when the item is
 * absent, not a media_add, or no longer pending (already uploaded) — exactly
 * the signal Step 5 uses to fall through to enqueueMediaRemove.
 *
 * @returns {Promise<boolean>} true = dequeued; false = caller must use the
 *   server-remove path (or nothing to do)
 */
export async function cancelPendingMediaAdd(outboxId) {
  try {
    await ensureOpen();
    const item = await fieldDb.syncQueue.get(outboxId);
    if (!item || item.type !== 'media_add' || item.status !== 'pending') return false;
    await fieldDb.syncQueue.delete(outboxId);
    return true;
  } catch (err) {
    console.error('fieldOutbox.cancelPendingMediaAdd failed:', err);
    throw err;
  }
}

/** All queue items in a given status, FIFO by time-prefixed outbox_id. */
export async function getQueueByStatus(status) {
  try {
    await ensureOpen();
    const items = await fieldDb.syncQueue.where('status').equals(status).toArray();
    return items.sort((a, b) => (a.outbox_id < b.outbox_id ? -1 : 1));
  } catch (err) {
    console.error('fieldOutbox.getQueueByStatus failed:', err);
    throw err;
  }
}

/** The pending queue, FIFO — what the 4.3b drain will replay. */
export function getPendingQueue() {
  return getQueueByStatus('pending');
}

/**
 * Mark a queue item's drain outcome (4.3b — the drain's sole writer).
 * Sets status and merges the confirmation/error/conflict payload onto the
 * item. For a 'saved' item this is the ONLY place the server's minted
 * part_number_ids / asset_tag_ids maps get captured (the idempotent re-save
 * returns {} maps) — 4.3c reconciles FROM the stored item, so losing them
 * here would orphan the mint mapping.
 *
 * @param {string} outboxId
 * @param {string} status — 'saved' | 'conflict' | 'not_found' | 'error' | 'failed' | 'pending'
 * @param {Object} [extra] — merged onto the item (e.g. {_etag, part_number_ids,
 *   asset_tag_ids, dropped_fields, updated_at} / {fields} / {error, retry_count})
 * @returns {Promise<void>}
 */
export async function markCaptureResult(outboxId, status, extra = {}) {
  try {
    await ensureOpen();
    const updated = await fieldDb.syncQueue.update(outboxId, { status, ...extra });
    if (!updated) {
      console.warn(`fieldOutbox.markCaptureResult: no item ${outboxId} to mark ${status}`);
    }
  } catch (err) {
    console.error('fieldOutbox.markCaptureResult failed:', err);
    throw err;
  }
}

/**
 * Path 1 "Edit ID & retry" (5.4 A-Phase 2 P1): revive a tool_create that
 * terminal-erred on the server's 409 duplicate guard, under a NEW
 * supplier_tool_id. Reuses the markCaptureResult 'pending' transition (the
 * drain's own retry ladder precedent) — the item keeps its outbox_id, so it
 * stays FIFO-AHEAD of its chained media_adds, which stay attached because
 * they key on the unchanged record_id/tool_id pending-* uuid (the D.4
 * re-key matches record_id, never supplier_tool_id). Media items are never
 * touched here.
 *
 * Order is load-bearing (spec): the record row is written BEFORE the queue
 * flip — a mid-op failure leaves the item still 'error' (recoverable, shown
 * in needs-attention), never 'pending' pointing at a stale row.
 * supplier_tool_id is create-stamped, NOT a captured field — it must not
 * enter dirty.
 *
 * The caller owns the grid thread (onRecordChange with the returned row)
 * and the 'tmg-field-enqueued' dispatch — this flip is not a producer put,
 * so notifyEnqueued does not fire on its own.
 *
 * @param {Object} item — the errored tool_create queue item
 * @param {string} newSupplierToolId
 * @returns {Promise<{retried: boolean, row: Object|null}>} retried=false =
 *   validation no-op (empty or unchanged id — nothing written); row = the
 *   updated records row when it still exists, else null (purged row: the
 *   queue flip still proceeds — the drain sends from the item's own fields).
 */
export async function retryDuplicateToolCreate(item, newSupplierToolId) {
  const next = String(newSupplierToolId ?? '').trim();
  if (!next || next === String(item.supplier_tool_id ?? '').trim()) {
    return { retried: false, row: null };
  }
  try {
    await ensureOpen();
    // (1) the optimistic row — working.supplier_tool_id only, never dirty
    const row = await fieldDb.records.get(item.record_id);
    if (row) {
      row.working = { ...(row.working || {}), supplier_tool_id: next };
      await fieldDb.records.put(row);
    }
    // (2) the queue flip — outbox_id preserved; 409 leftovers cleared
    await markCaptureResult(item.outbox_id, 'pending', {
      supplier_tool_id: next,
      error: null,
      retry_count: 0,
      existing_tool_id: null,
      message: null,
    });
    return { retried: true, row: row || null };
  } catch (err) {
    console.error('fieldOutbox.retryDuplicateToolCreate failed:', err);
    throw err;
  }
}

/**
 * Path 2 "Re-apply my edits" (D-2/D-4/D-5): resolve a field_capture CONFLICT
 * by re-basing the record row on server truth (rebaseRecordFromServer —
 * fetch-and-verify BEFORE any write, Phase 2) and re-enqueuing the capture
 * from the re-based row. The fresh payload carries the fresh etag
 * (buildCapturePayload reads row.etag) and the enqueue's D-5 coalescer
 * supersedes the lingering conflict item in the same transaction — NO
 * explicit conflict delete here (single mechanism). The rep's pending edits
 * (working/dirty) ride untouched: accept-server-then-overlay-mine.
 *
 * NO manual tmg-field-enqueued dispatch — enqueueFieldCapture is a producer
 * put and already fires notifyEnqueued() (Gate 3). Contrast Path 1's
 * retryDuplicateToolCreate, whose status FLIP needs the caller's manual
 * dispatch. A transport/HTTP throw out of the point-read propagates to the
 * caller with NOTHING mutated (write-last ordering inside the re-base).
 *
 * @param {Object} conflictItem — a syncQueue item
 * @param {string|null} callerEmail
 * @returns {Promise<{reapplied: boolean, reason?: string, row?: Object}>}
 *   false reasons: not_applicable (guard), row_absent / not_found / no_etag
 *   (re-base gate, propagated verbatim), empty_diff (defensive: the row had
 *   no dirt left to send — nothing enqueued, conflict item NOT superseded).
 */
export async function reapplyFieldCapture(conflictItem, callerEmail) {
  if (!conflictItem
    || conflictItem.type !== 'field_capture'
    || conflictItem.status !== 'conflict') {
    return { reapplied: false, reason: 'not_applicable' };
  }
  const rebase = await rebaseRecordFromServer(conflictItem.record_id, callerEmail);
  if (!rebase.rebased) {
    return { reapplied: false, reason: rebase.reason };
  }
  const outboxId = await enqueueFieldCapture(rebase.row);
  if (outboxId === null) {
    // Defensive: empty diff = nothing queued = the coalescer never ran, so
    // the conflict item still lingers — never report success on a no-op.
    return { reapplied: false, reason: 'empty_diff' };
  }
  return { reapplied: true, row: rebase.row };
}

/**
 * Path 3a "Retry" (drain part 3): revive an item in any ACTIONABLE_STATUSES
 * state — 'failed' (the retry-ladder cap, markHttpFailure/MAX_RETRIES=3),
 * and since D-39 'error' (auth_denied, tool_not_found, and every other
 * reason) and 'not_found' too. Until drain part 3 nothing could undo
 * 'failed'; until D-39 nothing could undo the other two.
 *
 * ⚠️ Widening did NOT change convergence, deliberately. The 401/403/404/409
 * arms leave retry_count untouched, so a retried 'error' item resets to 0 and
 * can re-error indefinitely without ever reaching 'failed'. That is a known,
 * accepted condition — the exit exists so a stuck row is not permanent, not
 * because a replay is expected to succeed. Do not add a counter or a cap here.
 *
 * Why a put and not markCaptureResult: markCaptureResult merges, so it can
 * set keys but never REMOVE them. A failed item carries whatever the sender
 * arm that killed it wrote — `error`/`retry_count` always, plus per-arm
 * leftovers (409's existing_tool_id/message, a conflict's fields, a 412's
 * _etag, parse_failed, …). Replaying with those still attached is how a
 * "retry" quietly sends a different item than the one first queued. So the
 * item is REBUILT from the enqueue allowlist and written with put, which
 * replaces the whole record.
 *
 * Values come from the LIVE item, never a reconstructed original:
 * reconcileConfirmations' D.4 dependent re-key (the syncQueue .modify in its
 * tool_create arm) re-keys record_id/tool_id in place when a chained
 * tool_create lands, and those rewritten values must survive.
 * (tool_create itself is dequeued in that same txn, so a surviving failed
 * tool_create was never re-keyed — and no sender reads item.tool_id for
 * that type.)
 *
 * outbox_id is preserved, so the item keeps its FIFO position and any
 * chained media_adds stay attached — the retryDuplicateToolCreate
 * precedent.
 *
 * The caller owns the 'tmg-field-enqueued' dispatch: this is a status flip,
 * not a producer put, so notifyEnqueued does not fire on its own (same
 * contract as retryDuplicateToolCreate / FieldSyncSheet.jsx:254).
 *
 * @param {string} outboxId
 * @returns {Promise<boolean>} true = rebuilt and queued; false = absent,
 *   not in ACTIONABLE_STATUSES, or an unknown type (never a partial rebuild)
 */
export async function retryFailedItem(outboxId) {
  try {
    await ensureOpen();
    const item = await fieldDb.syncQueue.get(outboxId);
    if (!item) {
      console.warn(`fieldOutbox.retryFailedItem: no item ${outboxId} to retry`);
      return false;
    }
    if (!ACTIONABLE_STATUSES.includes(item.status)) {
      console.warn(`fieldOutbox.retryFailedItem: ${outboxId} is '${item.status}', not one of ${ACTIONABLE_STATUSES.join('/')} — refused`);
      return false;
    }
    const allowed = ENQUEUE_FIELDS[item.type];
    if (!allowed) {
      console.error(`fieldOutbox.retryFailedItem: unknown type "${item.type}" on ${outboxId} — refused, no partial rebuild`);
      return false;
    }

    const rebuilt = {};
    ENQUEUE_COMMON.concat(allowed).forEach((key) => {
      if (key in item) rebuilt[key] = item[key];
    });
    rebuilt.status = 'pending';
    rebuilt.retry_count = 0;
    rebuilt.error = null;

    const dropped = Object.keys(item).filter((k) => !(k in rebuilt));
    await fieldDb.syncQueue.put(rebuilt);
    console.log(`[fieldOutbox] retry: rebuilt ${outboxId} (${item.type}), dropped ${dropped.length} leftover field(s)${dropped.length > 0 ? `: ${dropped.join(', ')}` : ''}`);
    return true;
  } catch (err) {
    console.error('fieldOutbox.retryFailedItem failed:', err);
    throw err;
  }
}

/**
 * Path 3b "Discard" (drain part 3): destroy an item in any
 * ACTIONABLE_STATUSES state. Pure dequeue, no server call — none of those
 * statuses means the write landed ('failed' exhausted the ladder, 'error' and
 * 'not_found' terminated on a server fact), so nothing upstream needs
 * undoing. This is the cancelPendingMediaAdd body with the guard moved from
 * (media_add + pending) to (any type + ACTIONABLE_STATUSES).
 *
 * ⚠️ IRREVERSIBLE. For a media_add the queue item holds the ONLY copy of the
 * photo (base64 blob_data — the upload never succeeded), so discarding it
 * destroys the capture. The byte count is logged for exactly that reason.
 * purgeFieldDB is deliberately NOT used here: it deletes the whole tmg-field
 * database, which is the wrong blast radius for one row.
 *
 * FIELD CANCEL PHASE 3 — ONE RULE: an actionable item on a pending- record
 * discards the RECORD, whatever the item's type, via discardPendingRecord.
 * Deleting only a tool_create stranded its pending- row (the 006604f defect:
 * Save said "no changes to send", re-Load kept it, its Tool ID was refused
 * as a duplicate). The rule is type-blind for the same reason the
 * primitive's step-3 sweep is: no path is known to put an actionable
 * non-tool_create item on a pending record, but that is an unproven
 * negative, and a type-narrowed rule would bet on it.
 *
 * The ACTIONABLE_STATUSES gate runs FIRST, on both paths: a stale confirm
 * must not discard a record whose item has since been retried back to
 * 'pending'. The primitive's own refusals (drain_active, already_created,
 * row_absent) are returned VERBATIM, never collapsed — they mean different
 * things and the caller advises differently on each.
 *
 * @param {string} outboxId
 * @returns {Promise<
 *   {discarded: true, scope: 'item'} |
 *   {discarded: true, scope: 'record', removedQueueItems: number} |
 *   {discarded: false, reason: 'absent'|'not_actionable'|'not_pending'
 *     |'drain_active'|'row_absent'|'already_created'}
 * >}
 */
export async function discardFailedItem(outboxId) {
  try {
    await ensureOpen();
    const item = await fieldDb.syncQueue.get(outboxId);
    if (!item) {
      console.warn(`fieldOutbox.discardFailedItem: no item ${outboxId} to discard`);
      return { discarded: false, reason: 'absent' };
    }
    if (!ACTIONABLE_STATUSES.includes(item.status)) {
      console.warn(`fieldOutbox.discardFailedItem: ${outboxId} is '${item.status}', not one of ${ACTIONABLE_STATUSES.join('/')} — refused`);
      return { discarded: false, reason: 'not_actionable' };
    }
    if (isPendingRecord(item.record_id)) {
      console.warn(`fieldOutbox.discardFailedItem: ${outboxId} (${item.type}) is on pending record ${item.record_id} — discarding the record`);
      const res = await discardPendingRecord(item.record_id);
      if (!res.removed) return { discarded: false, reason: res.reason };
      return { discarded: true, scope: 'record', removedQueueItems: res.removedQueueItems };
    }
    // byte_size is absent on items queued before D-7; the documented reader
    // contract (see enqueueMediaAdd) is to fall back to blob_data.length.
    const photoBytes = item.type === 'media_add'
      ? (item.byte_size ?? (item.blob_data ? item.blob_data.length : null))
      : null;
    console.warn(`[fieldOutbox] discard: destroying ${outboxId} (${item.type})${photoBytes === null ? '' : ` — includes ${photoBytes} bytes of photo data`}`);
    await fieldDb.syncQueue.delete(outboxId);
    return { discarded: true, scope: 'item' };
  } catch (err) {
    console.error('fieldOutbox.discardFailedItem failed:', err);
    throw err;
  }
}

/**
 * True when this record's tool_create has already been accepted by the
 * server. ONLY 'saved': a tool_create in error / conflict / not_found /
 * failed was refused, so no server document exists. Shared by the preflight
 * and the primitive so the two can never disagree about what "created" means.
 */
function hasSavedToolCreate(items) {
  return items.some((i) => i.type === 'tool_create' && i.status === 'saved');
}

/**
 * True when ANY tool_create for recordId is in the queue, WHATEVER its
 * status. Deliberately broader than hasSavedToolCreate, and the two are NOT
 * to be consolidated: that one asks "has the server accepted this tool?",
 * this one asks "does this record already have a create at all?". A
 * pending, saved, conflict, error, not_found or failed tool_create each
 * means the sheet's Send must not mint a second one — the actionable ones
 * already have Retry / Discard in Needs attention. Unlike hasSavedToolCreate
 * it filters by record_id itself, because its callers hold the whole queue.
 *
 * @param {Object[]} items — syncQueue items, any mix of records and types
 * @param {string} recordId
 * @returns {boolean}
 */
export function hasAnyToolCreate(items, recordId) {
  return items.some((i) => i.type === 'tool_create' && i.record_id === recordId);
}

/**
 * Field cancel preflight (spec v3 §5, amended 2B): what the Discard confirm
 * needs to know BEFORE it renders.
 *   photoCount — media_add items for the record (the only copies of those
 *                photos), via the record_id index.
 *   blocked    — 'already_created' when the record's tool_create is 'saved',
 *                so the UI never shows copy claiming it was never sent.
 * Read-only. discardPendingRecord re-checks on its own (defence in depth).
 *
 * @param {string} recordId
 * @returns {Promise<{photoCount: number, blocked: null|'already_created'}>}
 */
export async function getDiscardPreflight(recordId) {
  try {
    await ensureOpen();
    const items = await fieldDb.syncQueue.where('record_id').equals(recordId).toArray();
    return {
      photoCount: items.filter((i) => i.type === 'media_add').length,
      blocked: hasSavedToolCreate(items) ? 'already_created' : null,
    };
  } catch (err) {
    console.error('fieldOutbox.getDiscardPreflight failed:', err);
    throw err;
  }
}

/**
 * Field cancel (spec v3 §3): remove a record that has not been re-keyed to a
 * server identity, and EVERY queue item keyed to it. Local only — no server
 * call, no audit.
 *
 * 'pending-' means NOT YET RE-KEYED, not "never reached Cosmos" (spec §0 as
 * amended 2B). Between the drain marking a tool_create 'saved' and reconcile
 * re-keying the row — a window that is unbounded for a parse_failed create —
 * the tool exists server-side under a pending- row. That is 'already_created'.
 *
 * Refusals, in order, each { removed: false, reason }:
 *   'not_pending'     — recordId is not a temp id (isPendingRecord). A synced
 *                       record is never removed here.
 *   'drain_active'    — a drain cycle is running. An item being sent is still
 *                       'pending', so no status check can see it; without this
 *                       gate a tool_create POSTed a moment ago lands a tool in
 *                       Cosmos with no local row or item to reconcile it (§3.3).
 *   'row_absent'      — no fieldDb.records row for recordId.
 *   'already_created' — a tool_create for the record is 'saved': the server
 *                       has the tool. Checked inside the transaction, before
 *                       any delete.
 *
 * ONE transaction over records + syncQueue (§3.2), in this order: the
 * tool_create item, the media_add items, then every remaining item for the
 * record whatever its type or status, then the row. NO status or type filter
 * on the sweep — media_remove's impossibility is not established (audit §B),
 * so enumerating types would bet on a negative. Steps 1-2 are crash-ordering
 * only.
 *
 * ⚠️ IRREVERSIBLE. A media_add holds the ONLY copy of its photo, so the byte
 * count is logged, as discardFailedItem does.
 *
 * @param {string} recordId
 * @returns {Promise<{removed: boolean, removedQueueItems?: number, reason?: string}>}
 */
export async function discardPendingRecord(recordId) {
  if (!isPendingRecord(recordId)) {
    console.warn(`fieldOutbox.discardPendingRecord: ${recordId} is not a pending- record — refused`);
    return { removed: false, reason: 'not_pending' };
  }
  if (isFieldDrainActive()) {
    console.warn(`fieldOutbox.discardPendingRecord: drain active — refused ${recordId}`);
    return { removed: false, reason: 'drain_active' };
  }
  try {
    await ensureOpen();
    const result = await fieldDb.transaction('rw', fieldDb.records, fieldDb.syncQueue, async () => {
      // Row and created checks inside the transaction, so check and delete
      // see one state.
      const row = await fieldDb.records.get(recordId);
      if (!row) return { refused: 'row_absent' };
      const forRecord = () => fieldDb.syncQueue.where('record_id').equals(recordId);
      if (hasSavedToolCreate(await forRecord().toArray())) {
        return { refused: 'already_created' };
      }
      let removedQueueItems = 0;
      removedQueueItems += await forRecord().filter((i) => i.type === 'tool_create').delete();
      const media = await forRecord().filter((i) => i.type === 'media_add').toArray();
      const photoBytes = media.reduce(
        (sum, i) => sum + (i.byte_size ?? (i.blob_data ? i.blob_data.length : 0)), 0);
      await fieldDb.syncQueue.bulkDelete(media.map((i) => i.outbox_id));
      removedQueueItems += media.length;
      removedQueueItems += await forRecord().delete();
      await fieldDb.records.delete(recordId);
      return { removedQueueItems, photoCount: media.length, photoBytes };
    });
    if (result.refused === 'row_absent') {
      console.warn(`fieldOutbox.discardPendingRecord: no record ${recordId} to discard`);
      return { removed: false, reason: 'row_absent' };
    }
    if (result.refused === 'already_created') {
      console.warn(`fieldOutbox.discardPendingRecord: ${recordId} has a saved tool_create — the server has this tool; refused`);
      return { removed: false, reason: 'already_created' };
    }
    // Logged after commit, so it never reports a destruction that rolled back.
    console.warn(`[fieldOutbox] discard: destroyed pending record ${recordId} and ${result.removedQueueItems} queue item(s)${result.photoCount === 0 ? '' : ` — includes ${result.photoCount} photo(s), ${result.photoBytes} bytes of photo data`}`);
    return { removed: true, removedQueueItems: result.removedQueueItems };
  } catch (err) {
    console.error('fieldOutbox.discardPendingRecord failed:', err);
    throw err;
  }
}

/** Entire queue in FIFO pk order (diagnostics + tests). */
export async function getQueue() {
  try {
    await ensureOpen();
    return await fieldDb.syncQueue.orderBy(':id').toArray();
  } catch (err) {
    console.error('fieldOutbox.getQueue failed:', err);
    throw err;
  }
}
